// Pure helpers for the workflow / group / routing admin UI. No React, no I/O.
import type { Condition } from '@/lib/expr'
import type { ConditionField } from '../../components/ConditionEditor'
import type { TimelineStep } from '../../components/WorkflowTimeline'
import type {
    AssigneeSpec, DecisionKind, FieldOption, Form, FormDefinition, FormField, Group, RoutingRule,
    WorkflowDefinition, WorkflowStep,
} from '../../types'

export const ALL_DECISIONS: DecisionKind[] = ['accept', 'acknowledge', 'approve_with_conditions', 'refuse']
export const MAX_RULE_DEPTH = 8

// ---------- generic list helpers ----------

/** Returns a copy with the item at `from` moved to `to` (clamped; no-op when out of range). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
    const out = list.slice()
    if (from < 0 || from >= out.length) return out
    const target = Math.max(0, Math.min(out.length - 1, to))
    if (target === from) return out
    const [item] = out.splice(from, 1)
    out.splice(target, 0, item)
    return out
}

export const removeAt = <T,>(list: readonly T[], i: number): T[] => list.filter((_, idx) => idx !== i)
export const replaceAt = <T,>(list: readonly T[], i: number, item: T): T[] => list.map((x, idx) => (idx === i ? item : x))

// ---------- steps ----------

/** Renumbers step.order to 1..n following list order. */
export function renumberSteps<T extends { order: number }>(steps: readonly T[]): T[] {
    return steps.map((s, i) => ({ ...s, order: i + 1 }))
}

export function newStep(n: number): WorkflowStep {
    return { order: n, name: `Step ${n}`, mode: 'any', assignees: [] }
}

export function emptyDefinition(): WorkflowDefinition {
    return {
        steps: [newStep(1)],
        decisions: ['accept', 'approve_with_conditions', 'refuse'],
        requireAttestation: false,
        shareComment: false,
        autoEmail: true,
    }
}

// ---------- assignee specs ----------

export type AssigneeKind = 'user' | 'group' | 'submitter_manager' | 'field' | 'conditional'

export function assigneeKind(s: AssigneeSpec): AssigneeKind {
    if (s.kind === 'user') return 'user'
    if (s.kind === 'group') return 'group'
    if (s.rule === 'submitter_manager') return 'submitter_manager'
    if (s.rule === 'field') return 'field'
    return 'conditional'
}

export interface LabelCtx {
    groups: Pick<Group, 'id' | 'name'>[]
    userRefFields: { key: string; label: string }[]
}

export function assigneeLabel(s: AssigneeSpec, ctx: LabelCtx): string {
    switch (assigneeKind(s)) {
        case 'user': return (s as { name?: string; userId: string }).name || (s as { userId: string }).userId
        case 'group': {
            const id = (s as { groupId: string }).groupId
            return ctx.groups.find((g) => g.id === id)?.name ?? `Unknown group (${id.slice(0, 8)})`
        }
        case 'submitter_manager': return "Submitter's manager"
        case 'field': {
            const key = (s as { field: string }).field
            return ctx.userRefFields.find((f) => f.key === key)?.label ?? `Form field ${key}`
        }
        default: return 'Conditional'
    }
}

export function summarizeAssignees(list: readonly AssigneeSpec[], ctx: LabelCtx): string {
    if (list.length === 0) return 'No one'
    return list.map((s) => assigneeLabel(s, ctx)).join(', ')
}

type Conditional = Extract<AssigneeSpec, { rule: 'conditional' }>
type Case = Conditional['cases'][number]

export const newConditional = (): Conditional => ({
    kind: 'rule', rule: 'conditional', cases: [{ when: { all: [] }, assignees: [] }], else: [],
})

export function addCase(s: Conditional): Conditional {
    return { ...s, cases: [...s.cases, { when: { all: [] }, assignees: [] }] }
}
export function updateCase(s: Conditional, i: number, patch: Partial<Case>): Conditional {
    return { ...s, cases: s.cases.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }
}
export function removeCase(s: Conditional, i: number): Conditional {
    return { ...s, cases: removeAt(s.cases, i) }
}
export function moveCase(s: Conditional, from: number, to: number): Conditional {
    return { ...s, cases: moveItem(s.cases, from, to) }
}
export function setElse(s: Conditional, list: AssigneeSpec[]): Conditional {
    return { ...s, else: list }
}

/** Every group id referenced anywhere (including nested conditional branches). */
export function groupIdsIn(specs: readonly AssigneeSpec[]): string[] {
    const out = new Set<string>()
    const walk = (list: readonly AssigneeSpec[]) => {
        for (const s of list) {
            if (s.kind === 'group') out.add(s.groupId)
            else if (s.kind === 'rule' && s.rule === 'conditional') {
                s.cases.forEach((c) => walk(c.assignees))
                walk(s.else ?? [])
            }
        }
    }
    walk(specs)
    return [...out]
}

export const groupIdsInDefinition = (d: WorkflowDefinition): string[] =>
    [...new Set(d.steps.flatMap((s) => groupIdsIn(s.assignees)))]

// ---------- forms -> fields ----------

export interface FormWithDefinition {
    form: Pick<Form, 'id' | 'slug' | 'name'>
    definition: FormDefinition | null
}

export interface FieldRef {
    formSlug: string
    formName: string
    field: FormField
}

/** All answer fields of the given forms (html blocks excluded). */
export function collectFieldRefs(forms: readonly FormWithDefinition[]): FieldRef[] {
    const out: FieldRef[] = []
    for (const f of forms) {
        for (const p of f.definition?.pages ?? []) {
            for (const field of p.fields) {
                if (field.type === 'html') continue
                out.push({ formSlug: f.form.slug, formName: f.form.name, field })
            }
        }
    }
    return out
}

function dedupeByKey(refs: readonly FieldRef[]): ConditionField[] {
    const byKey = new Map<string, { names: string[]; label: string; field: FormField; options: FieldOption[] }>()
    for (const r of refs) {
        const cur = byKey.get(r.field.key)
        if (!cur) {
            byKey.set(r.field.key, { names: [r.formName], label: r.field.label, field: r.field, options: [...(r.field.options ?? [])] })
        } else {
            if (!cur.names.includes(r.formName)) cur.names.push(r.formName)
            for (const o of r.field.options ?? []) if (!cur.options.some((x) => x.value === o.value)) cur.options.push(o)
        }
    }
    return [...byKey.entries()].map(([key, v]) => ({
        key,
        label: `${v.names.join(' / ')} · ${v.label}`,
        group: v.names.join(' / '),
        name: v.label,
        type: v.field.type,
        options: v.options.length ? v.options : undefined,
    }))
}

/** Fields usable in conditions: label "Form · Field", deduped by key. */
export function conditionFields(refs: readonly FieldRef[]): ConditionField[] {
    return dedupeByKey(refs)
}

/** user_reference fields for the "person named on the form" rule. */
export function userRefFields(refs: readonly FieldRef[]): { key: string; label: string }[] {
    return dedupeByKey(refs.filter((r) => r.field.type === 'user_reference')).map((f) => ({ key: f.key, label: f.label }))
}

/** Routing conditions: the synthetic $form field first, then all answer fields. */
export function routingFields(forms: readonly FormWithDefinition[]): ConditionField[] {
    const slugs = forms.map((f) => ({ value: f.form.slug, label: f.form.name }))
    return [{ key: '$form', label: 'Form', type: 'form', options: slugs }, ...conditionFields(collectFieldRefs(forms))]
}

// ---------- routing rules ----------

export interface KeyedRule extends RoutingRule { _key: string }

let keySeq = 0
export const nextKey = (prefix = 'k') => `${prefix}${++keySeq}`

export function toKeyed(rules: readonly RoutingRule[]): KeyedRule[] {
    return rules.map((r) => ({ ...r, _key: r.id ?? nextKey('r') }))
}

/** Strip client keys and imply position from order. */
export function toPayload(rules: readonly KeyedRule[]): RoutingRule[] {
    return rules.map(({ _key: _k, ...r }, i) => ({ ...r, position: i + 1 }))
}

// ---------- validation (mirrors workflow.Definition.Validate, friendlier) ----------

export function validateAssignees(where: string, specs: readonly AssigneeSpec[], depth = 0): string[] {
    const errs: string[] = []
    if (depth > MAX_RULE_DEPTH) return [`${where}: assignee rules are nested too deeply`]
    for (const s of specs) {
        if (s.kind === 'user' && !s.userId) errs.push(`${where}: a person assignee has no person selected`)
        else if (s.kind === 'group' && !s.groupId) errs.push(`${where}: a group assignee has no group selected`)
        else if (s.kind === 'rule') {
            if (s.rule === 'field' && !s.field) errs.push(`${where}: a "person named on the form" assignee has no field selected`)
            if (s.rule === 'conditional') {
                if (s.cases.length === 0) errs.push(`${where}: a conditional assignee needs at least one case`)
                s.cases.forEach((c, i) => {
                    errs.push(...validateAssignees(`${where}, case ${i + 1}`, c.assignees, depth + 1))
                    if (c.assignees.length === 0) errs.push(`${where}, case ${i + 1}: add at least one assignee`)
                })
                errs.push(...validateAssignees(`${where}, otherwise`, s.else ?? [], depth + 1))
            }
        }
    }
    return errs
}

export function validateWorkflowDraft(name: string, def: WorkflowDefinition): string[] {
    const errs: string[] = []
    if (!name.trim()) errs.push('Give the workflow a name')
    if (def.steps.length === 0) errs.push('Add at least one step')
    def.steps.forEach((s, i) => {
        const where = `Step ${i + 1}${s.name ? ` (${s.name})` : ''}`
        if (!s.name.trim()) errs.push(`${where}: give the step a name`)
        if (s.assignees.length === 0) errs.push(`${where}: add at least one assignee`)
        errs.push(...validateAssignees(where, s.assignees))
    })
    if (def.decisions.length === 0) errs.push('Choose at least one decision option')
    return errs
}

// ---------- live preview ----------

export interface PreviewCtx extends LabelCtx {
    /** Cached group members by group id. */
    members: Record<string, { id: string; name: string; email: string }[] | undefined>
}

type PreviewPerson = TimelineStep['assignees'][number]

/** Statically resolvable people for a list of specs; rules become labelled placeholders. */
export function previewPeople(specs: readonly AssigneeSpec[], ctx: PreviewCtx): PreviewPerson[] {
    const out = new Map<string, PreviewPerson>()
    const add = (p: PreviewPerson) => { if (!out.has(p.id)) out.set(p.id, p) }
    const walk = (list: readonly AssigneeSpec[], viaPrefix = '') => {
        for (const s of list) {
            switch (assigneeKind(s)) {
                case 'user': {
                    const u = s as { userId: string; name?: string }
                    add({ id: u.userId, name: u.name || u.userId, email: '', via: viaPrefix || 'user' })
                    break
                }
                case 'group': {
                    const id = (s as { groupId: string }).groupId
                    const gname = assigneeLabel(s, ctx)
                    const mem = ctx.members[id]
                    if (!mem || mem.length === 0) {
                        add({ id: `group:${id}`, name: mem ? `${gname} (empty)` : `${gname} (loading…)`, email: '', via: `group:${gname}` })
                    } else mem.forEach((m) => add({ id: m.id, name: m.name, email: m.email, via: `group:${gname}` }))
                    break
                }
                case 'submitter_manager':
                    add({ id: 'rule:submitter_manager', name: "Submitter's manager", email: '', via: "submitter's manager" })
                    break
                case 'field': {
                    const key = (s as { field: string }).field
                    add({ id: `rule:field:${key}`, name: assigneeLabel(s, ctx), email: '', via: `field:${key}` })
                    break
                }
                default: {
                    const c = s as Conditional
                    c.cases.forEach((cs) => walk(cs.assignees, 'conditional'))
                    walk(c.else ?? [], 'conditional')
                    if (c.cases.every((cs) => cs.assignees.length === 0) && (c.else ?? []).length === 0) {
                        add({ id: `rule:cond:${out.size}`, name: 'Conditional assignee', email: '', via: 'conditional' })
                    }
                }
            }
        }
    }
    walk(specs)
    return [...out.values()]
}

export function previewSteps(def: WorkflowDefinition, ctx: PreviewCtx): TimelineStep[] {
    return def.steps.map((s, i) => ({
        step: i + 1,
        name: s.name,
        mode: s.mode,
        status: 'pending' as const,
        assignees: previewPeople(s.assignees, ctx),
    }))
}

/** Map the routing test API's steps (keyed by `order`) to TimelineSteps. */
export function toTimelineSteps(
    steps: { order: number; name: string; mode: 'any' | 'all'; status: TimelineStep['status']; assignees: PreviewPerson[] }[],
): TimelineStep[] {
    return steps.map((s) => ({ step: s.order, name: s.name, mode: s.mode, status: s.status, assignees: s.assignees ?? [] }))
}

export function condOrAlways(c: Condition | undefined): Condition {
    return c ?? { all: [] }
}
